import { AxiosError, type AxiosRequestConfig } from 'axios'

import { serverErrorMessage } from './serverError'

export const RATE_LIMIT_STATUS = 429

/**
 * How long to hold off when the answer carries no `Retry-After` — which is the usual case, so
 * this is the number that actually applies.
 *
 * Short on purpose: what has to stop is the *burst*, and a window this size stops it. Blocking
 * the whole app for as long as a refusal might really last would be worse than the hammering
 * it prevents. The next attempt simply meets another refusal and opens a new window — one
 * request per half-minute instead of one per widget per render.
 */
const DEFAULT_COOLDOWN_MS = 30_000

/** A `Retry-After` cannot lock the app up: whatever it says, the field is capped here. */
const MAX_COOLDOWN_MS = 5 * 60 * 1000

export const RATE_LIMITED_CODE = 'ERR_RATE_LIMITED'

let blockedUntil = 0

let reason = ''

/**
 * `Retry-After` in milliseconds — the header is either delta-seconds or an HTTP-date, and
 * both spellings are legal. Anything else is not worth guessing at.
 */
function parseRetryAfter(header: unknown): number | null {
  if (typeof header !== 'string') return null

  const value = header.trim()

  if (!value) return null
  if (/^\d+$/.test(value)) return Number(value) * 1000

  const at = Date.parse(value)

  return Number.isNaN(at) ? null : at - Date.now()
}

export function rateLimitedFor(): number {
  return Math.max(0, blockedUntil - Date.now())
}

/** Whether this error is a refusal for asking too often — ours or the server's. */
export function isRateLimitError(error: unknown): boolean {
  return (
    (error as AxiosError | undefined)?.response?.status === RATE_LIMIT_STATUS
  )
}

/**
 * Open (or extend) the cooldown a 429 asks for.
 *
 * Returns whether this one *opened* it. A page renders a dozen widgets and every one of
 * them can be refused within the same second; only the first has anything to tell the
 * user, and the rest would be a stack of identical toasts over a screen that already
 * shows the failure.
 */
export function noteRateLimited(error: unknown): boolean {
  // One of ours, bounced by the request interceptor rather than answered by the server.
  // It must not extend the window it was refused by: an interceptor rejection travels the
  // same path as a real answer, so every held-back request would push the deadline another
  // half-minute out and a window opened once would never close again.
  if ((error as AxiosError | undefined)?.code === RATE_LIMITED_CODE)
    return false

  const opensWindow = rateLimitedFor() === 0
  const header = (error as AxiosError | undefined)?.response?.headers?.[
    'retry-after'
  ]

  const asked = parseRetryAfter(header)
  const waitMs = Math.min(
    Math.max(asked ?? DEFAULT_COOLDOWN_MS, 1000),
    MAX_COOLDOWN_MS
  )

  // The later of the two: a second refusal arriving mid-window must not shorten it.
  blockedUntil = Math.max(blockedUntil, Date.now() + waitMs)
  reason = serverErrorMessage(error, '')

  return opensWindow
}

export function rateLimitMessage(): string {
  const seconds = Math.ceil(rateLimitedFor() / 1000)
  const wait = `Please try again in ${seconds} second${seconds === 1 ? '' : 's'}.`

  return reason ? `${reason} ${wait}` : `Too many requests. ${wait}`
}

/**
 * The refusal a request held back locally is rejected with.
 *
 * Shaped as a real 429 rather than as a bare `Error`, because everything downstream reads
 * failures through `error.response`: `serverErrorMessage` for the sentence a table shows,
 * the query client's `retry` predicate, and the poller's own check. A different shape
 * here would mean teaching all three about a second kind of error, and the one thing they
 * must agree on is that this is the same refusal that came from the server.
 */
export function rateLimitedError(config: AxiosRequestConfig): AxiosError {
  const message = rateLimitMessage()

  return new AxiosError(
    message,
    RATE_LIMITED_CODE,
    config as AxiosError['config'],
    null,
    {
      status: RATE_LIMIT_STATUS,
      statusText: 'Too Many Requests',
      data: { error: message },
      headers: {},
      config: config as never
    } as AxiosError['response']
  )
}
